import { Building2, FileCode, Globe, User } from "lucide-react";
import type { Binding } from "@/app/types";

/**
 * Comprehensive binding styling configuration
 */
const BINDING_STYLES = {
  project: {
    // Blue theme - highest priority, user-specific
    background: "bg-blue-600",
    hoverBackground: "hover:bg-blue-600/90",
    ring: "ring-blue-600",
    badgeText: "text-white",
    badgeBorder: "border-blue-600/40",
    sectionBackground: "bg-blue-100 dark:bg-blue-900/20",
    sectionIcon: "text-blue-600 dark:text-blue-400",
    sectionTitle: "text-blue-900 dark:text-blue-100",
    sectionBorder: "border-blue-200 dark:border-blue-800",
    sectionEmptyIcon: "text-blue-400",
  },
  tenant: {
    // Purple theme - shared across projects
    background: "bg-purple-600",
    hoverBackground: "hover:bg-purple-600/90",
    ring: "ring-purple-600",
    badgeText: "text-white",
    badgeBorder: "border-purple-600/40",
    sectionBackground: "bg-purple-100 dark:bg-purple-900/20",
    sectionIcon: "text-purple-600 dark:text-purple-400",
    sectionTitle: "text-purple-900 dark:text-purple-100",
    sectionBorder: "border-purple-200 dark:border-purple-800",
    sectionEmptyIcon: "text-purple-400",
  },
  static: {
    // Red theme - code-defined, matches primary
    background: "bg-red-600",
    hoverBackground: "hover:bg-red-600/90",
    ring: "ring-red-600",
    badgeText: "text-white",
    badgeBorder: "border-red-600/40",
    sectionBackground: "bg-red-100 dark:bg-red-900/20",
    sectionIcon: "text-red-600 dark:text-red-400",
    sectionTitle: "text-red-900 dark:text-red-100",
    sectionBorder: "border-red-200 dark:border-red-800",
    sectionEmptyIcon: "text-red-400",
  },
  account: {
    // Orange theme - account-level
    background: "bg-orange-600",
    hoverBackground: "hover:bg-orange-600/90",
    ring: "ring-orange-600",
    badgeText: "text-white",
    badgeBorder: "border-orange-600/40",
    sectionBackground: "bg-orange-100 dark:bg-orange-900/20",
    sectionIcon: "text-orange-600 dark:text-orange-400",
    sectionTitle: "text-orange-900 dark:text-orange-100",
    sectionBorder: "border-orange-200 dark:border-orange-800",
    sectionEmptyIcon: "text-orange-400",
  },
} as const;

/**
 * Legacy theme configuration for backwards compatibility
 */
const BINDING_THEMES = {
  project: {
    light: "blue-600",
    dark: "blue-400",
    background: "blue-100",
    backgroundDark: "blue-900/20",
    border: "blue-200",
    borderDark: "blue-800",
    text: "blue-900",
    textDark: "blue-100",
  },
  tenant: {
    light: "purple-600",
    dark: "purple-400",
    background: "purple-100",
    backgroundDark: "purple-900/20",
    border: "purple-200",
    borderDark: "purple-800",
    text: "purple-900",
    textDark: "purple-100",
  },
  static: {
    light: "red-600",
    dark: "red-400",
    background: "red-100",
    backgroundDark: "red-900/20",
    border: "red-200",
    borderDark: "red-800",
    text: "red-900",
    textDark: "red-100",
  },
  account: {
    light: "orange-600",
    dark: "orange-400",
    background: "orange-100",
    backgroundDark: "orange-900/20",
    border: "orange-200",
    borderDark: "orange-800",
    text: "orange-900",
    textDark: "orange-100",
  },
} as const;

/**
 * Get consistent styling classes for binding types
 * Used for badges, sections, and other UI elements that need binding-specific colors
 */
const getBindingClass = (binding: Binding) =>
  BINDING_STYLES[binding] || BINDING_STYLES.static;

/**
 * Get binding display name with proper capitalization
 */
const getBindingDisplayName = (binding: Binding): string => {
  const names: Record<Binding, string> = {
    project: "Project",
    tenant: "Organization",
    static: "Global",
    account: "Account",
  };

  return names[binding] || "Unknown";
};

/**
 * Get the appropriate icon for each binding type
 */
const getBindingIcon = (binding: Binding) => {
  const icons = {
    project: Building2,
    tenant: Globe,
    static: FileCode,
    account: User,
  };

  return icons[binding] || FileCode;
};

/**
 * Get generic binding description for any resource type
 */
const getBindingDescription = (binding: Binding): string => {
  const descriptions: Record<Binding, string> = {
    project: "Available only in this project",
    tenant: "Shared with all projects in this organization",
    static: "Global to all organizations, projects, and accounts",
    account: "Available for this account",
  };

  return descriptions[binding] || "Unknown binding type";
};

/**
 * Returns all the basic info you need for UI components
 */
export const getBindingInfo = (binding: Binding) => {
  const theme = BINDING_THEMES[binding] || BINDING_THEMES.static;
  const classes = getBindingClass(binding);
  const displayName = getBindingDisplayName(binding);

  return {
    displayName,
    icon: getBindingIcon(binding),
    description: getBindingDescription(binding),
    theme,
    classes,
    emptyMessage: `No ${displayName} resources found.`,
  };
};
